import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import {
  fetchDetectionChains,
  fetchDetectionChain,
  enableDetectionChain,
  disableDetectionChain,
  createDetectionChain,
  updateDetectionChain,
  deleteDetectionChain
} from "./api/detectionChainsAPI";
const detectionChainsListKey = (params) => ["detection-chains", "list", params];
const detectionChainDetailKey = (name) => ["detection-chains", "detail", name];
export const buildBulkActionMessage = (verb, chainName, counts) => {
  const total = counts.affected + counts.alreadyInTargetState + counts.skipped + counts.failed;
  const skippedNote = counts.skipped ? i18n.t("notifications.bulk_skipped_note", { ns: "enterprise_security_sigma", skipped: counts.skipped }) : "";
  const failedNote = counts.failed ? i18n.t("notifications.bulk_failed_note", { ns: "enterprise_security_sigma", failed: counts.failed }) : "";
  return i18n.t(verb === "Enabled" ? "notifications.bulk_message_enabled" : "notifications.bulk_message_disabled", {
    ns: "enterprise_security_sigma",
    affected: counts.affected,
    total,
    chainName,
    skippedNote,
    failedNote
  });
};
export const buildBulkActionNotification = (verb, chainName, counts) => {
  const message = buildBulkActionMessage(verb, chainName, counts);
  const isEnable = verb === "Enabled";
  const blocked = counts.skipped > 0 || counts.failed > 0;
  const permissionHint = counts.skipped ? i18n.t("notifications.bulk_permission_hint", { ns: "enterprise_security_sigma" }) : "";
  if (counts.affected === 0 && blocked) {
    return {
      severity: "error",
      title: i18n.t(isEnable ? "notifications.could_not_enable_chain" : "notifications.could_not_disable_chain", {
        ns: "enterprise_security_sigma"
      }),
      message: `${message}.${permissionHint}`
    };
  }
  if (blocked) {
    return {
      severity: "error",
      title: i18n.t(isEnable ? "notifications.chain_partially_enabled" : "notifications.chain_partially_disabled", {
        ns: "enterprise_security_sigma"
      }),
      message: `${message}.${permissionHint}`
    };
  }
  return {
    severity: "success",
    title: i18n.t(isEnable ? "notifications.chain_enabled" : "notifications.chain_disabled", {
      ns: "enterprise_security_sigma"
    }),
    message
  };
};
export const useGetDetectionChains = (params) => useQuery({
  queryKey: detectionChainsListKey(params),
  queryFn: () => fetchDetectionChains(params),
  placeholderData: keepPreviousData
});
export const useGetDetectionChain = (name) => useQuery({
  queryKey: detectionChainDetailKey(name != null ? name : ""),
  queryFn: () => fetchDetectionChain(name),
  enabled: !!name
});
export const useEnableDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name) => enableDetectionChain(name),
    onSuccess: (response, name) => {
      const { severity, title, message } = buildBulkActionNotification("Enabled", name, {
        affected: response.enabled_count,
        alreadyInTargetState: response.already_enabled_count,
        skipped: response.skipped_count,
        failed: response.failed_count
      });
      UserNotification[severity](message, title);
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, name) => {
      UserNotification.error(
        i18n.t("notifications.enable_chain_failed", { ns: "enterprise_security_sigma", name, error: error.message }),
        i18n.t("notifications.enable_chain_failed_title", { ns: "enterprise_security_sigma" })
      );
    }
  });
};
export const useCreateDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createDetectionChain(payload),
    onSuccess: (chain) => {
      UserNotification.success(
        i18n.t("notifications.chain_created", { ns: "enterprise_security_sigma", name: chain.name }),
        i18n.t("notifications.chain_created_title", { ns: "enterprise_security_sigma" })
      );
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error) => {
      UserNotification.error(
        i18n.t("notifications.create_chain_failed", { ns: "enterprise_security_sigma", error: error.message }),
        i18n.t("notifications.create_chain_failed_title", { ns: "enterprise_security_sigma" })
      );
    }
  });
};
export const useUpdateDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateDetectionChain(id, payload),
    onSuccess: (chain) => {
      UserNotification.success(
        i18n.t("notifications.chain_updated", { ns: "enterprise_security_sigma", name: chain.name }),
        i18n.t("notifications.chain_updated_title", { ns: "enterprise_security_sigma" })
      );
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error) => {
      UserNotification.error(
        i18n.t("notifications.update_chain_failed", { ns: "enterprise_security_sigma", error: error.message }),
        i18n.t("notifications.update_chain_failed_title", { ns: "enterprise_security_sigma" })
      );
    }
  });
};
export const useDeleteDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => deleteDetectionChain(id),
    onSuccess: (_response, { name }) => {
      UserNotification.success(
        i18n.t("notifications.chain_deleted", { ns: "enterprise_security_sigma", name }),
        i18n.t("notifications.chain_deleted_title", { ns: "enterprise_security_sigma" })
      );
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, { name }) => {
      UserNotification.error(
        i18n.t("notifications.delete_chain_failed", { ns: "enterprise_security_sigma", name, error: error.message }),
        i18n.t("notifications.delete_chain_failed_title", { ns: "enterprise_security_sigma" })
      );
    }
  });
};
export const useDisableDetectionChain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name) => disableDetectionChain(name),
    onSuccess: (response, name) => {
      const { severity, title, message } = buildBulkActionNotification("Disabled", name, {
        affected: response.disabled_count,
        alreadyInTargetState: response.already_disabled_count,
        skipped: response.skipped_count,
        failed: response.failed_count
      });
      UserNotification[severity](message, title);
      queryClient.invalidateQueries({ queryKey: ["detection-chains"] });
    },
    onError: (error, name) => {
      UserNotification.error(
        i18n.t("notifications.disable_chain_failed", { ns: "enterprise_security_sigma", name, error: error.message }),
        i18n.t("notifications.disable_chain_failed_title", { ns: "enterprise_security_sigma" })
      );
    }
  });
};
